import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBars, FaTimes } from 'react-icons/fa';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  return (
    <StyledHeader scrolled={scrolled}>
      <div className="container">
        <Nav>
          <Logo
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link to="/">Portfolio</Link>
          </Logo>

          <MenuButton onClick={toggleMenu}>
            {isOpen ? <FaTimes /> : <FaBars />}
          </MenuButton>

          <NavLinks
            className={isOpen ? 'open' : ''}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <NavItem>
              <NavLink to="/" onClick={() => setIsOpen(false)}>Home</NavLink>
            </NavItem>
            <NavItem>
              <NavLink to="/about" onClick={() => setIsOpen(false)}>About</NavLink>
            </NavItem>
            <NavItem>
              <NavLink to="/experience" onClick={() => setIsOpen(false)}>Experience</NavLink>
            </NavItem>
            <NavItem>
              <NavLink to="/projects" onClick={() => setIsOpen(false)}>Projects</NavLink>
            </NavItem>
            <NavItem>
              <NavLink to="/skills" onClick={() => setIsOpen(false)}>Skills</NavLink>
            </NavItem>
            <NavItem>
              <NavLink to="/contact" onClick={() => setIsOpen(false)}>Contact</NavLink>
            </NavItem>
          </NavLinks>
        </Nav>
      </div>

      <AnimatePresence>
        {isOpen && (
          <MobileMenu
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "100vh", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <MobileNavLinks>
              <MobileNavItem>
                <MobileNavLink to="/" onClick={() => setIsOpen(false)}>Home</MobileNavLink>
              </MobileNavItem>
              <MobileNavItem>
                <MobileNavLink to="/about" onClick={() => setIsOpen(false)}>About</MobileNavLink>
              </MobileNavItem>
              <MobileNavItem>
                <MobileNavLink to="/experience" onClick={() => setIsOpen(false)}>Experience</MobileNavLink>
              </MobileNavItem>
              <MobileNavItem>
                <MobileNavLink to="/projects" onClick={() => setIsOpen(false)}>Projects</MobileNavLink>
              </MobileNavItem>
              <MobileNavItem>
                <MobileNavLink to="/skills" onClick={() => setIsOpen(false)}>Skills</MobileNavLink>
              </MobileNavItem>
              <MobileNavItem>
                <MobileNavLink to="/contact" onClick={() => setIsOpen(false)}>Contact</MobileNavLink>
              </MobileNavItem>
            </MobileNavLinks>
          </MobileMenu>
        )}
      </AnimatePresence>
    </StyledHeader>
  );
};

// Styled Components
const StyledHeader = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  transition: all 0.3s ease;
  background-color: ${props => props.scrolled ? 'rgba(10, 25, 41, 0.85)' : 'transparent'};
  backdrop-filter: ${props => props.scrolled ? 'blur(10px)' : 'none'};
  box-shadow: ${props => props.scrolled ? '0 10px 30px -10px rgba(0, 10, 30, 0.5)' : 'none'};
  padding: ${props => props.scrolled ? '15px 0' : '20px 0'};

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
  }
`;

const Nav = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled(motion.div)`
  a {
    color: var(--accent);
    font-size: 1.5rem;
    font-weight: 700;
    text-decoration: none;
    transition: color 0.3s ease;
    
    &:hover {
      color: var(--text-light);
      text-shadow: 0 0 8px rgba(30, 136, 229, 0.5);
    }
  }
`;

const MenuButton = styled.button`
  display: none;
  background: transparent;
  border: none;
  color: var(--accent);
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 1001;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const NavLinks = styled(motion.ul)`
  display: flex;
  list-style: none;
  padding: 0;
  margin: 0;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavItem = styled.li`
  margin: 0 15px;
`;

const NavLink = styled(Link)`
  color: var(--text-light);
  text-decoration: none;
  font-size: 1rem;
  transition: all 0.3s ease;
  padding: 5px 10px;
  position: relative;
  
  &:hover {
    color: var(--accent);
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background-color: var(--accent);
    transition: width 0.3s ease, box-shadow 0.3s ease;
  }
  
  &:hover::after {
    width: 100%;
  }
`;

const MobileMenu = styled(motion.div)`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  background-color: #112240;
  z-index: 1000;
  overflow: hidden;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileNavLinks = styled.ul`
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  list-style: none;
  padding: 0;
  margin: 0;
`;

const MobileNavItem = styled.li`
  margin: 15px 0;
  opacity: 0;
  animation: fadeIn 0.5s forwards;
  animation-delay: calc(0.1s * var(--i));
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const MobileNavLink = styled(Link)`
  color: #ccd6f6;
  text-decoration: none;
  font-size: 1.5rem;
  transition: color 0.3s ease;
  
  &:hover {
    color: #64ffda;
  }
`;

export default Header;
